import { useEffect, useState } from 'react';
import { useDealsStore } from './store/deals';
import { useUIStore } from './store/ui';
import { health } from './services/api';
import { TopBar } from './components/shell/TopBar';
import { TabBar } from './components/shell/TabBar';
import { Workspace } from './components/shell/Workspace';
import { DealIndex } from './components/shell/DealIndex';
import { EmptyState } from './components/shell/EmptyState';

type BootState = 'booting' | 'unauthorized' | 'ready' | 'error';

export function App() {
  const [bootState, setBootState] = useState<BootState>('booting');
  const [bootError, setBootError] = useState<string>('');
  const loadAllDeals = useDealsStore(s => s.loadAllDeals);
  const loadUI = useUIStore(s => s.load);
  const openDeal = useUIStore(s => s.openDeal);
  const deals = useDealsStore(s => s.deals);
  const openTabs = useUIStore(s => s.openTabs);
  const activeDealId = useUIStore(s => s.activeDealId);
  const showDealIndex = useUIStore(s => s.showDealIndex);

  useEffect(() => {
    (async () => {
      try {
        // 1. Health check (verifies auth + bindings)
        const h = await health.check();
        if (!h.ok) { setBootState('error'); setBootError('Health check failed'); return; }

        // 2. Load deals + UI state in parallel
        await Promise.all([loadAllDeals(), loadUI()]);

        setBootState('ready');
      } catch (e: any) {
        const msg = e?.message || String(e);
        if (/40[13]/.test(msg)) setBootState('unauthorized');
        else { setBootState('error'); setBootError(msg); }
      }
    })();
  }, [loadAllDeals, loadUI]);

  // Open Molloy by default once booted
  useEffect(() => {
    if (bootState === 'ready' && openTabs.length === 0 && deals.length > 0) {
      const molloy = deals.find(d => d.id === 'deal_molloy') ?? deals[0];
      openDeal(molloy.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootState, openTabs.length, deals.length]);

  if (bootState === 'booting') {
    return (
      <div className="app">
        <div className="empty-state" style={{ paddingTop: 200 }}>
          <strong>Booting Timberline…</strong>
          Authenticating · loading deals · syncing buyers
        </div>
      </div>
    );
  }
  if (bootState === 'unauthorized') {
    return (
      <div className="app">
        <div className="empty-state" style={{ paddingTop: 200 }}>
          <strong>Not authorized</strong>
          Sign in via Cloudflare Access. If you should have access, contact admin.
        </div>
      </div>
    );
  }
  if (bootState === 'error') {
    return (
      <div className="app">
        <div className="empty-state" style={{ paddingTop: 200 }}>
          <strong>Couldn't reach the workbench backend</strong>
          {bootError || 'Unknown error'}
          <div style={{ marginTop: 16, fontFamily: 'var(--mono)', fontSize: 11 }}>
            Check that D1, R2, and KV bindings are configured in Pages.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <TopBar />
      <TabBar />
      {activeDealId ? <Workspace /> : <EmptyState />}
      {showDealIndex && <DealIndex />}
    </div>
  );
}
